/**
 * Moteur de formatage Rhino pour panneaux de randonnée
 * Version JavaScript - Réplique EXACTE des règles Python (formatting_rules.py)
 *
 * Architecture: Moteur pur sans dépendances DOM/Konva
 * Utilisation: Calcul et formatage de texte selon les règles de production
 */

class RhinoFormattingEngine {
  constructor() {
    // ============================================================================
    // CONSTANTES - LIMITES DE LARGEUR (mm)
    // ============================================================================
    this.RECT_MAX_WIDTH = 165.0;  // Largeur max texte rectangulaire
    this.SQUARE_MAX_WIDTH = 80.0;  // Largeur max texte carré
    this.RECT_SAFE_WIDTH_15MM = 165.0;  // Seuil pour forcer 2 lignes

    // ============================================================================
    // CONSTANTES - ESPACEMENTS (mm)
    // ============================================================================
    this.SPACING_DEFAULT = 3.0;
    this.SPACING_WITH_ASCENDERS = 2.0;
    this.SPACING_WITH_DESCENDERS = 1.5;
    this.LINE_SPACING_MULTIPLIER = 1.5;

    // ============================================================================
    // CONSTANTES - TAILLES DE TEXTE (mm)
    // ============================================================================
    // Format carré
    this.SQUARE_TITLE_SIZE_MAX = 15.0;
    this.SQUARE_TITLE_SIZE_MEDIUM = 12.5;
    this.SQUARE_TITLE_SIZE_MIN = 10.0;
    this.SQUARE_TITLE_MAX_LINES = 3;

    this.SQUARE_SECONDARY_SIZE_SHORT = 10.0;
    this.SQUARE_SECONDARY_SIZE_MEDIUM = 7.5;
    this.SQUARE_SECONDARY_SIZE_LONG = 5.0;
    this.SQUARE_SECONDARY_THRESHOLD_SHORT = 12;
    this.SQUARE_SECONDARY_THRESHOLD_MEDIUM = 40;

    // Format rectangulaire
    this.RECT_TITLE_SIZE_MAX = 20.0;
    this.RECT_TITLE_SIZE_MEDIUM = 15.0;
    this.RECT_TITLE_SIZE_MIN = 12.5;
    this.RECT_MANY_UPPERCASE_THRESHOLD = 10;

    this.RECT_SECONDARY_SIZE_SHORT = 7.5;
    this.RECT_SECONDARY_SIZE_LONG = 5.0;
    this.RECT_SECONDARY_THRESHOLD = 30;

    // ============================================================================
    // CONSTANTES - CARACTÈRES SPÉCIAUX
    // ============================================================================
    this.DESCENDER_LETTERS = new Set(['g', 'j', 'y', 'p', 'q']);
    this.ASCENDER_LETTERS = new Set(['b', 'd', 'f', 'h', 'k', 'l', 't']);

    this.HEART_VARIANTS = [
      '<3', '❤️', '❤', '♥️', '♥', '💕', '💖', '💗', '💓', '💞', '💝',
      '🤍', '🖤', '💙', '💚', '💛', '🧡', '💜', '🤎'
    ];
    this.NORMALIZED_HEART = '♥';

    // Pré-compiler les regex pour les variantes de cœurs (optimisation)
    this._heartRegexes = this.HEART_VARIANTS.map(heart => {
      const escapedHeart = heart.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(escapedHeart, 'gu');
    });

    // Cache pour les largeurs de caractères
    this._charWidths = null;
    this._widthsLoaded = false;
  }

  /**
   * Charge les largeurs de caractères depuis le fichier JSON
   * @returns {Promise<boolean>} True si chargé avec succès
   */
  async loadCharacterWidths() {
    if (this._widthsLoaded) {
      return true;
    }

    try {
      // Utiliser l'URL CDN Shopify fournie par le template
      const fontWidthsUrl = window.PANNEAU_ASSET_URLS?.fontWidths || '/assets/basier_square_widths.json';
      const response = await fetch(fontWidthsUrl);
      if (!response.ok) {
        console.error('[RhinoEngine] Erreur chargement largeurs caractères');
        return false;
      }

      this._charWidths = await response.json();
      this._widthsLoaded = true;
      return true;
    } catch (error) {
      console.error('[RhinoEngine] Erreur:', error);
      return false;
    }
  }

  /**
   * Calcule la largeur EXACTE d'un texte en mm
   * Utilise les métriques de police Basier Square chargées depuis le JSON
   * @param {string} text - Le texte à mesurer
   * @param {number} height - Hauteur du texte en mm
   * @returns {number} Largeur totale en mm
   */
  calculateTextWidth(text, height) {
    // Validation des entrées
    if (text == null || text === '') {
      return 0;
    }

    if (typeof text !== 'string') {
      console.warn('[RhinoEngine] calculateTextWidth called with non-string:', typeof text);
      text = String(text);
    }

    if (!this._charWidths) {
      // Fallback si pas de données: estimation conservatrice
      return text.length * height * 0.65;
    }

    const heightKey = height.toString();

    if (!this._charWidths[heightKey]) {
      // Si cette hauteur n'est pas calibrée, estimer proportionnellement
      if (this._charWidths['20.0']) {
        const ratio = height / 20.0;
        let total = 0;
        for (const char of text) {
          const charWidth = this._charWidths['20.0'][char] || (height * 0.65);
          total += charWidth * ratio;
        }
        return total;
      }
      // Fallback ultime
      return text.length * height * 0.65;
    }

    // Calculer la largeur totale en additionnant chaque caractère
    let totalWidth = 0;
    for (const char of text) {
      const charWidth = this._charWidths[heightKey][char] || (height * 0.5);
      totalWidth += charWidth;
    }

    return totalWidth;
  }

  /**
   * Nettoie le texte (espaces parasites, etc.)
   * @param {string} text - Le texte à nettoyer
   * @returns {string} Le texte nettoyé
   */
  cleanText(text) {
    if (!text) return text;

    // Trim général
    text = text.trim();

    // Remplacer espaces multiples par un seul
    const lines = text.split('\n');
    const cleanedLines = lines.map(line => line.split(/\s+/).join(' '));

    return cleanedLines.join('\n');
  }

  /**
   * Normalise les emojis (convertit cœurs, supprime autres emojis)
   * @param {string} text - Le texte avec emojis
   * @returns {string} Le texte normalisé
   */
  normalizeEmojis(text) {
    if (!text) return text;

    // D'abord, remplacer tous les types de cœurs par ♥
    // Utiliser les regex pré-compilées pour optimiser les performances
    this._heartRegexes.forEach(regex => {
      text = text.replace(regex, this.NORMALIZED_HEART);
    });

    // Supprimer TOUS les autres emojis avec une regex complète
    // Cette regex couvre tous les emojis Unicode (y compris modificateurs et sélecteurs de variation)
    // Elle préserve le cœur normalisé ♥ (U+2665)
    const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F910}-\u{1F96B}\u{1F980}-\u{1F9E0}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}]/gu;

    text = text.replace(emojiRegex, function(match) {
      // Garder le cœur normalisé ♥ (U+2665)
      if (match === '\u2665' || match === '♥') {
        return match;
      }
      // Supprimer tous les autres emojis
      return '';
    });

    // Nettoyer les espaces multiples qui peuvent rester après suppression d'emojis
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  }

  /**
   * Enveloppe le texte selon une largeur maximale EN MM (pas en caractères)
   * Respecte les règles de coupure intelligente (parenthèses, apostrophes)
   *
   * @param {Array<string>} words - Liste des mots
   * @param {number} maxWidthMm - Largeur maximale en mm
   * @param {number} textHeight - Hauteur du texte en mm
   * @returns {Array<string>} Liste de lignes
   */
  wrapTextByWidth(words, maxWidthMm, textHeight) {
    const lines = [];
    let currentLine = '';
    let parenDepth = 0;

    for (const word of words) {
      // Compter les parenthèses
      parenDepth = Math.max(0, parenDepth + (word.match(/\(/g) || []).length - (word.match(/\)/g) || []).length);

      // Calculer la largeur si on ajoutait ce mot
      const potentialLine = currentLine ? currentLine + ' ' + word : word;
      const potentialWidth = this.calculateTextWidth(potentialLine, textHeight);

      // Vérifier si on peut ajouter ce mot
      if (potentialWidth <= maxWidthMm) {
        currentLine = potentialLine;
      } else {
        // On doit couper, mais vérifier les règles :

        // Règle 1 : Ne pas couper si on est dans des parenthèses
        if (parenDepth > 0) {
          currentLine = potentialLine;
          continue;
        }

        // Règle 2 : Ne pas couper après une apostrophe
        if (currentLine.trim().endsWith("'")) {
          currentLine = potentialLine;
          continue;
        }

        // Sinon, couper normalement
        if (currentLine.trim()) {
          lines.push(currentLine.trim());
        }
        currentLine = word;
      }
    }

    // Ajouter la dernière ligne
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    return lines;
  }

  /**
   * Vérifie si la dernière ligne contient des jambages inférieurs (descenders)
   * @param {string} text - Le texte à analyser
   * @returns {boolean} True si descenders présents
   */
  hasDescendersInLastLine(text) {
    const lines = text.trim().split('\n');
    if (!lines.length) return false;

    const lastLine = lines[lines.length - 1];
    return Array.from(lastLine).some(letter => this.DESCENDER_LETTERS.has(letter));
  }

  /**
   * Vérifie si la première ligne contient des jambages supérieurs (ascenders)
   * @param {string} text - Le texte à analyser
   * @returns {boolean} True si ascenders présents
   */
  hasAscendersInFirstLine(text) {
    const lines = text.trim().split('\n');
    if (!lines.length) return false;

    const firstLine = lines[0];
    return Array.from(firstLine).some(letter => this.ASCENDER_LETTERS.has(letter));
  }

  /**
   * Calcule l'espacement optimal entre titre et texte secondaire
   * @param {string} titleText - Le titre
   * @param {string} secondaryText - Le texte secondaire
   * @returns {number} Espacement en mm
   */
  calculateTextSpacing(titleText, secondaryText) {
    if (!titleText || !secondaryText) {
      return this.SPACING_DEFAULT;
    }

    const hasDescenders = this.hasDescendersInLastLine(titleText);
    const hasAscenders = this.hasAscendersInFirstLine(secondaryText);

    if (hasDescenders && hasAscenders) {
      return this.SPACING_WITH_ASCENDERS;
    } else if (hasDescenders) {
      return this.SPACING_WITH_DESCENDERS;
    } else {
      return this.SPACING_DEFAULT;
    }
  }

  /**
   * Vérifie si une ligne contient beaucoup de majuscules
   * @param {string} text - Le texte à analyser
   * @returns {boolean} True si > seuil majuscules
   */
  hasManyUppercaseOnLine(text) {
    const lines = text.trim().split('\n');
    for (const line of lines) {
      const uppercaseCount = (line.match(/[A-Z]/g) || []).length;
      if (uppercaseCount > this.RECT_MANY_UPPERCASE_THRESHOLD) {
        return true;
      }
    }
    return false;
  }

  /**
   * Formate le texte principal pour format CARRÉ
   * Applique EXACTEMENT les règles Python
   *
   * @param {string} title - Le titre à formater
   * @returns {Object} { formattedTitle, textHeight, lines }
   */
  formatSquareTitle(title) {
    // Gérer le cas du texte vide
    if (!title || !title.trim()) {
      return {
        formattedTitle: '',
        textHeight: this.SQUARE_TITLE_SIZE_MAX,
        lines: ['']
      };
    }

    const words = title.split(/\s+/);

    // Étape 1 : Essayer avec taille maximale
    let currentTextHeight = this.SQUARE_TITLE_SIZE_MAX;
    let titleWidth = this.calculateTextWidth(title, currentTextHeight);

    let lines;

    // Étape 2 : Vérifier si le titre dépasse la limite
    if (titleWidth > this.SQUARE_MAX_WIDTH) {
      // Couper par largeur réelle
      lines = this.wrapTextByWidth(words, this.SQUARE_MAX_WIDTH, currentTextHeight);

      // Étape 3 : Si > 3 lignes, passer à taille moyenne
      if (lines.length > this.SQUARE_TITLE_MAX_LINES) {
        currentTextHeight = this.SQUARE_TITLE_SIZE_MEDIUM;
        lines = this.wrapTextByWidth(words, this.SQUARE_MAX_WIDTH, currentTextHeight);

        // Étape 4 : Si toujours trop, passer à taille minimale
        if (lines.length > this.SQUARE_TITLE_MAX_LINES) {
          currentTextHeight = this.SQUARE_TITLE_SIZE_MIN;
          lines = this.wrapTextByWidth(words, this.SQUARE_MAX_WIDTH, currentTextHeight);

          // Étape 5 : SÉCURITÉ - Forcer 3-4 lignes si toujours trop
          if (lines.length > 4) {
            const third = Math.floor(words.length / 3);
            lines = [
              words.slice(0, third).join(' '),
              words.slice(third, 2 * third).join(' '),
              words.slice(2 * third).join(' ')
            ];
          }
        }
      }
    } else {
      // Le titre tient sur une ligne
      lines = [title];
    }

    return {
      formattedTitle: lines.length > 0 ? lines.join('\n') : '',
      textHeight: currentTextHeight,
      lines: lines
    };
  }

  /**
   * Formate le texte principal pour format RECTANGULAIRE
   * Applique EXACTEMENT les règles Python
   *
   * @param {string} title - Le titre à formater
   * @returns {Object} { formattedTitle, textHeight, lines }
   */
  formatRectTitle(title) {
    // Gérer le cas du texte vide
    if (!title || !title.trim()) {
      return {
        formattedTitle: '',
        textHeight: this.RECT_TITLE_SIZE_MAX,
        lines: ['']
      };
    }

    const originalWords = title.split(/\s+/);

    // Étape 1 : Essayer avec taille max sur UNE ligne
    let textHeight = this.RECT_TITLE_SIZE_MAX;
    let titleWidth = this.calculateTextWidth(title, textHeight);
    let formattedTitle = title;

    if (titleWidth > this.RECT_MAX_WIDTH) {
      // Trop large → passer à taille moyenne
      textHeight = this.RECT_TITLE_SIZE_MEDIUM;
      titleWidth = this.calculateTextWidth(title, textHeight);

      if (titleWidth > this.RECT_MAX_WIDTH) {
        // Trop large sur une ligne → wrap
        let lines = this.wrapTextByWidth(originalWords, this.RECT_MAX_WIDTH, textHeight);
        formattedTitle = lines.join('\n');

        // Vérifier si même après wrap, une ligne dépasse
        const linesTooWide = lines.some(l => this.calculateTextWidth(l, textHeight) > this.RECT_MAX_WIDTH);

        if (linesTooWide) {
          // Dernier recours : taille minimale
          textHeight = this.RECT_TITLE_SIZE_MIN;
          lines = this.wrapTextByWidth(originalWords, this.RECT_MAX_WIDTH, textHeight);
          formattedTitle = lines.join('\n');

          // Vérification finale
          const stillTooWide = lines.some(l => this.calculateTextWidth(l, textHeight) > this.RECT_MAX_WIDTH);
          if (stillTooWide) {
            // Forcer coupure équitable en 3 lignes
            const third = Math.floor(originalWords.length / 3);
            lines = [
              originalWords.slice(0, third).join(' '),
              originalWords.slice(third, 2 * third).join(' '),
              originalWords.slice(2 * third).join(' ')
            ];
            formattedTitle = lines.join('\n');
          }
        }
      } else if (titleWidth > this.RECT_SAFE_WIDTH_15MM) {
        // Tient sur une ligne MAIS trop proche → forcer 2 lignes
        const lines = this.wrapTextByWidth(originalWords, this.RECT_SAFE_WIDTH_15MM, textHeight);
        formattedTitle = lines.join('\n');
      }
    } else {
      // Tient sur une ligne à taille max
      // Vérifier si beaucoup de majuscules
      if (this.hasManyUppercaseOnLine(formattedTitle)) {
        textHeight = this.RECT_TITLE_SIZE_MEDIUM;
      }
    }

    return {
      formattedTitle: formattedTitle || '',
      textHeight: textHeight,
      lines: formattedTitle ? formattedTitle.split('\n') : []
    };
  }

  /**
   * Calcule la taille du texte secondaire selon les règles
   *
   * @param {string} secondaryText - Le texte secondaire
   * @param {string} dimension - 'carré' ou 'rectangle'
   * @param {Object} titleResult - Résultat du formatage du titre
   * @returns {number} Hauteur du texte secondaire en mm
   */
  calculateSecondaryTextSize(secondaryText, dimension, titleResult) {
    if (!secondaryText || !secondaryText.trim()) {
      return 0;
    }

    if (dimension === 'carré') {
      // Format carré : hauteur selon nombre de caractères
      const charCount = secondaryText.length;
      if (charCount > this.SQUARE_SECONDARY_THRESHOLD_MEDIUM) {
        return this.SQUARE_SECONDARY_SIZE_LONG;
      } else if (charCount > this.SQUARE_SECONDARY_THRESHOLD_SHORT) {
        return this.SQUARE_SECONDARY_SIZE_MEDIUM;
      } else {
        return this.SQUARE_SECONDARY_SIZE_SHORT;
      }
    } else {
      // Format rectangulaire : hauteur selon nombre de caractères
      const charCount = secondaryText.length;
      if (charCount > this.RECT_SECONDARY_THRESHOLD) {
        return this.RECT_SECONDARY_SIZE_LONG;  // 5.0mm pour texte > 30 caractères
      } else {
        return this.RECT_SECONDARY_SIZE_SHORT;  // 7.5mm pour texte <= 30 caractères
      }
    }
  }

  /**
   * Formate un panneau complet (titre + texte secondaire)
   * POINT D'ENTRÉE PRINCIPAL
   *
   * @param {Object} options - { texte_principal, texte_secondaire, dimension }
   * @returns {Object} Résultat complet du formatage
   */
  format(options) {
    const {
      texte_principal = '',
      texte_secondaire = '',
      dimension = 'rectangle'  // 'rectangle' ou 'carré'
    } = options;

    // Nettoyer et normaliser les textes
    let cleanedTitle = this.cleanText(texte_principal);
    cleanedTitle = this.normalizeEmojis(cleanedTitle);

    let cleanedSecondary = this.cleanText(texte_secondaire);
    cleanedSecondary = this.normalizeEmojis(cleanedSecondary);

    // Formater le titre selon le format
    const titleResult = dimension === 'carré'
      ? this.formatSquareTitle(cleanedTitle)
      : this.formatRectTitle(cleanedTitle);

    // Calculer la taille du texte secondaire
    const secondaryHeight = this.calculateSecondaryTextSize(
      cleanedSecondary,
      dimension,
      titleResult
    );

    // Calculer l'espacement entre titre et secondaire
    const spacing = this.calculateTextSpacing(
      titleResult.formattedTitle,
      cleanedSecondary
    );

    return {
      // Texte principal
      title: {
        text: titleResult.formattedTitle,
        height: titleResult.textHeight,
        lines: titleResult.lines,
        width: Math.max(...titleResult.lines.map(l =>
          this.calculateTextWidth(l, titleResult.textHeight)
        ))
      },
      // Texte secondaire
      secondary: {
        text: cleanedSecondary,
        height: secondaryHeight,
        width: secondaryHeight > 0 ? this.calculateTextWidth(cleanedSecondary, secondaryHeight) : 0
      },
      // Métadonnées
      spacing: spacing,
      dimension: dimension,
      // Flags de debug
      debug: {
        hasDescenders: this.hasDescendersInLastLine(titleResult.formattedTitle),
        hasAscenders: cleanedSecondary ? this.hasAscendersInFirstLine(cleanedSecondary) : false,
        manyUppercase: this.hasManyUppercaseOnLine(titleResult.formattedTitle)
      }
    };
  }
}

// Export pour utilisation dans Shopify
window.RhinoFormattingEngine = RhinoFormattingEngine;
